import { now, ToneAudioBuffer, ToneAudioBuffers, Transport } from 'tone';
import { groupBy, isNumber, some } from 'lodash';

import { Drum, DrumHit, MarchSubscriber } from '../core';
import { dryDest, nativeAudioContext, wetDest } from './transport';

import bdUrl from '../assets/sounds/tr808/bd1075.mp3';
import cbUrl from '../assets/sounds/tr808/cb.mp3';
import chUrl from '../assets/sounds/tr808/ch.mp3';
import clUrl from '../assets/sounds/tr808/cl.mp3';
import cpUrl from '../assets/sounds/tr808/cp.mp3';
import cy5050Url from '../assets/sounds/tr808/cy5050.mp3';
import hc50Url from '../assets/sounds/tr808/hc50.mp3';
import ht50Url from '../assets/sounds/tr808/ht50.mp3';
import lc50Url from '../assets/sounds/tr808/lc50.mp3';
import lt50Url from '../assets/sounds/tr808/lt50.mp3';
import maUrl from '../assets/sounds/tr808/ma.mp3';
import mc50Url from '../assets/sounds/tr808/mc50.mp3';
import mt50Url from '../assets/sounds/tr808/mt50.mp3';
import oh50Url from '../assets/sounds/tr808/oh50.mp3';
import rsUrl from '../assets/sounds/tr808/rs.mp3';
import sd5050Url from '../assets/sounds/tr808/sd5050.mp3';
import trigOutUrl from '../assets/sounds/tr808/trigout.mp3';


let bd = new ToneAudioBuffer(bdUrl);
let kit = new ToneAudioBuffers({
    CB: cbUrl,
    CH: chUrl,
    CL: clUrl,
    CP: cpUrl,
    CY: cy5050Url,
    HC: hc50Url,
    HT: ht50Url,
    LC: lc50Url,
    LT: lt50Url,
    MA: maUrl,
    MC: mc50Url,
    MT: mt50Url,
    OH: oh50Url,
    RS: rsUrl,
    SD: sd5050Url,
    trigOut: trigOutUrl
});
let lastStarts = new Map<Drum, number>();
let shuffle = 0;
let fillState: 'noFill' | 'queued' | 'fill' = 'noFill';


let wetGain = nativeAudioContext.createGain();
wetGain.gain.value = 0.75;
wetGain.connect(wetDest);

let dryGain = nativeAudioContext.createGain();
dryGain.gain.value = 0.75;
dryGain.connect(dryDest);

let currentSchedule: number | null = null;
let currentSequences: { [timeStep: string]: DrumHit[] }[] = [];
let currentSequenceLength = 16;
let currentFillSequence: { [timeStep: string]: DrumHit[] } = {};
let marchSubscribers: MarchSubscriber[] = [];

export function applySequences(seqs: readonly (readonly DrumHit[])[], seqLength: number) {
    currentSequences = seqs.map(seq => groupBy(seq, s => s.timeStep));
    currentSequenceLength = seqLength;
}
export function applyFillSequence(seq: readonly DrumHit[]) {
    currentFillSequence = groupBy(seq, s => s.timeStep);
}
export function triggerFill() {
    if (fillState === 'noFill') fillState = 'queued';
}

export function setShuffle(newShuffle: number) {
    shuffle = newShuffle;
}

export function start(startTime: number, startBeat: number) {
    if (isNumber(currentSchedule)) return;
    let sequence = 0;
    let step = startBeat;
    currentSchedule = Transport.scheduleRepeat(time => {
        let subdiv = getSubDiv(step);
        if (subdiv === 0) {
            if (fillState === 'queued') {
                fillState = 'fill';
            } else if (fillState === 'fill') {
                fillState = 'noFill';
            }
        }

        let shuffleThis = subdiv % 2 !== 0;
        let effectiveShuffle = shuffleThis ? shuffle : 0;
        let sixteenthSec = Transport.toSeconds('16n');
        let effectiveTime = time + effectiveShuffle * sixteenthSec;

        if (effectiveShuffle >= 0) {
            playHits(subdiv, sequence, effectiveTime);
        }

        if (shuffle < 0 && subdiv % 2 === 0) {
            let nextSubdiv = subdiv + 1;
            let nextEffectiveShuffle = 1 - shuffle;
            let nextEffectiveTime = time + nextEffectiveShuffle * sixteenthSec;
            playHits(nextSubdiv, sequence, nextEffectiveTime);
        }

        step++;
        if (step % currentSequenceLength === 0) {
            sequence++;
        }
    }, '16n', startTime);
}

function playHits(subdiv: number, sequence: number, time: number) {
    let hits = (fillState === 'fill' ? currentFillSequence[subdiv] : currentSequences[sequence % currentSequences.length][subdiv]) ?? [];
    let accented = some(hits, h => h.note === 'AC');
    if (accented) {
        dryGain.gain.setValueAtTime(1.0, time);
        wetGain.gain.setValueAtTime(1.0, time);
    } else {
        dryGain.gain.setValueAtTime(0.75, time);
        wetGain.gain.setValueAtTime(0.75, time);
    }
    let hasKick = false;
    for (let hit of hits) {
        if (hit.note !== "AC") {
            if (!lastStarts.has(hit.note) || lastStarts.get(hit.note)! < time) {
                try {
                    if (hit.note === 'BD') {
                        hasKick = true;
                        if (bd.loaded) {
                            let src = nativeAudioContext.createBufferSource();
                            src.buffer = bd.get()!;
                            src.connect(dryGain);
                            src.start(time);
                        }
                    } else {
                        let buf = kit.get(hit.note)!;
                        if (buf.loaded) {
                            let src = nativeAudioContext.createBufferSource();
                            src.buffer = buf.get()!;
                            src.connect(wetGain);
                            src.start(time);
                        }
                    }
                    lastStarts.set(hit.note, time);
                } catch (e) {
                    console.warn(e);
                }
            }
        }
    }
    if (accented && !hasKick) {
        let src = nativeAudioContext.createBufferSource();
        src.buffer = kit.get('trigOut').get()!;
        let gain = nativeAudioContext.createGain();
        gain.gain.value = 0.5;
        src.connect(gain);
        gain.connect(wetGain);
        src.start(time);
    }
    if (marchSubscribers.length) {
        let hitDrums = hits.map(h => h.note);
        for (let sub of marchSubscribers) {
            sub(subdiv, getSubDiv(subdiv - 1), hitDrums, time, fillState === 'fill');
        }
    }
}

function getSubDiv(step: number) {
    let subDiv = step % currentSequenceLength;
    if (subDiv < 0) subDiv += currentSequenceLength;
    return subDiv;
}

export function stop() {
    if (isNumber(currentSchedule)) {
        Transport.clear(currentSchedule);
        currentSchedule = null;
        for (let sub of marchSubscribers) {
            sub(-1, -1, [], now(), false);
        }
    }
}

export function subscribeToMarch(subscriber: MarchSubscriber) {
    marchSubscribers.push(subscriber);
    return () => {
        let idx = marchSubscribers.indexOf(subscriber);
        if (idx >= 0) {
            marchSubscribers.splice(idx, 1);
        }
    }
}